/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { bindAll } from 'bind-event-listener';
import ReactDOM from 'react-dom';

import {
	findClosestSnap,
	generateDefaultGuidelines,
	getGuidelineSnaps,
	getGuidelinesWithHighlights,
} from '@atlaskit/editor-common/guideline';
import { getParentNodeWidth } from '@atlaskit/editor-common/node-width';
import type { Dimensions, HandleResize, Position, Snap } from '@atlaskit/editor-common/resizer';
import { ResizerNext } from '@atlaskit/editor-common/resizer';
import type { ExtractInjectionAPI, UiComponentFactoryParams } from '@atlaskit/editor-common/types';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { findDomRefAtPos, type ContentNodeWithPos } from '@atlaskit/editor-prosemirror/utils';
import {
	akEditorDefaultLayoutWidth,
	akEditorFullWidthLayoutWidth,
	breakoutWideScaleRatio,
} from '@atlaskit/editor-shared-styles';
import { getParameters } from '@atlaskit/native-embeds-common';
import type { AlignmentValue } from '@atlaskit/native-embeds-common';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core';

import type { EditorPluginNativeEmbedsPlugin } from '../nativeEmbedsPluginType';
import { createUpdateAlignmentCommand } from '../pm-plugins/commands/alignment';
import { createUpdateWidthCommand, getWidth } from '../pm-plugins/commands/resize';
import { NATIVE_EMBED_PLUGIN_IS_RESIZING_KEY } from '../pm-plugins/main';
import { getSelectedNativeEmbedExtension } from '../pm-plugins/utils/getSelectedNativeEmbedExtension';

import { calcMaxWidth } from './utils';

export const resizerNextTestId = 'nativeEmbed.resizerNext.testid';

type ResizableNativeEmbedProps = UiComponentFactoryParams & {
	pluginInjectionApi: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined;
};

interface NodeRect {
	height: number;
	left: number;
	top: number;
	width: number;
}

// ---------------------------------------------------------------------------
// Styles (compiled CSS)
// ---------------------------------------------------------------------------

const resizerWrapperStyles = css({
	position: 'absolute',
	pointerEvents: 'auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'& > div': {
		position: 'absolute',
		pointerEvents: 'auto',
		maxWidth: 'var(--ak-editor-max-container-width)',
		minWidth: 'min(24px, 100%)',
	},
});

const resizerPlaceholderStyles = css({
	width: '100%',
	pointerEvents: 'none',
});

// TODO: CNS-24595 - use the constants file.
const MIN_WIDTH = 200;
const SNAP_GAP = 8;
const CSS_VAR_NATIVE_EMBED_WIDTH = '--native-embed-width';

/**
 * Maps AlignmentValue to RichMediaLayout for guideline snapping.
 * AlignmentValue uses 'left'/'right', but RichMediaLayout expects 'align-start'/'align-end'.
 */
const mapAlignmentToLayout = (
	alignment: AlignmentValue,
): 'align-start' | 'align-end' | 'center' | 'wrap-left' | 'wrap-right' => {
	if (alignment === 'left') {
		return 'align-start';
	}
	if (alignment === 'right') {
		return 'align-end';
	}
	// 'center', 'wrap-left', 'wrap-right' pass through unchanged
	return alignment;
};

export const ResizableNativeEmbed = (props: ResizableNativeEmbedProps) => {
	const { pluginInjectionApi, disabled, editorView: view, appearance } = props;
	const disableHandles = !!disabled;

	const [selectedNativeEmbed, setSelectedNativeEmbed] = useState<ContentNodeWithPos | null>(null);
	const [currentAlignment, setCurrentAlignment] = useState<AlignmentValue>('center');

	// -----------------------------------------------------------------------
	// Reactive width plugin subscription
	// -----------------------------------------------------------------------

	const [lineLength, setLineLength] = useState(() => {
		return (
			pluginInjectionApi?.width.sharedState.currentState()?.lineLength || akEditorDefaultLayoutWidth
		);
	});
	const [containerWidth, setContainerWidth] = useState(() => {
		return (
			pluginInjectionApi?.width.sharedState.currentState()?.width || akEditorDefaultLayoutWidth
		);
	});

	useEffect(() => {
		const unsubscribe = pluginInjectionApi?.width.sharedState.onChange(({ nextSharedState }) => {
			if (nextSharedState) {
				setLineLength(nextSharedState.lineLength || akEditorDefaultLayoutWidth);
				setContainerWidth(nextSharedState.width || akEditorDefaultLayoutWidth);
			}
		});
		return () => {
			unsubscribe?.();
		};
	}, [pluginInjectionApi]);

	// -----------------------------------------------------------------------
	// Reactive native embed selection subscription
	// -----------------------------------------------------------------------

	const [selectedEmbedPos, setSelectedEmbedPos] = useState<number | null>(() => {
		return (
			pluginInjectionApi?.editorPluginNativeEmbeds?.sharedState?.currentState()?.selectedEmbedPos ??
			null
		);
	});

	// Guard to prevent shared state updates during resize (PM transactions
	// dispatched during drag would trigger re-renders and position jumps).
	// Also activated on mousedown on resize handles to prevent the editor
	// from deselecting the embed before the drag starts.
	const isResizingRef = useRef(false);

	useEffect(() => {
		const initial = pluginInjectionApi?.editorPluginNativeEmbeds?.sharedState?.currentState();
		setSelectedEmbedPos(initial?.selectedEmbedPos ?? null);
		setCurrentAlignment(initial?.selectedEmbedAlignment ?? 'center');

		const unsub = pluginInjectionApi?.editorPluginNativeEmbeds?.sharedState?.onChange?.(
			({ nextSharedState }) => {
				if (isResizingRef.current) {
					return;
				}
				setSelectedEmbedPos(nextSharedState?.selectedEmbedPos ?? null);
				if (nextSharedState?.selectedEmbedAlignment) {
					setCurrentAlignment(nextSharedState.selectedEmbedAlignment);
				}
			},
		);
		return () => {
			unsub?.();
		};
	}, [pluginInjectionApi]);

	useEffect(() => {
		const selected = view && getSelectedNativeEmbedExtension(view.state);

		// Batch all state updates together to avoid chaining
		ReactDOM.flushSync(() => {
			if (selected) {
				const domNode = findDomRefAtPos(selected.pos, view.domAtPos.bind(view));
				const element =
					domNode instanceof HTMLElement ? domNode : (domNode.parentElement as HTMLElement | null);
				if (element) {
					const container =
						(element.querySelector('.extension-container') as HTMLElement | null) ??
						(element.closest('.extension-container') as HTMLElement | null) ??
						element;

					// Also grab the inner EmbedFrameWrapper container so we can
					// update its inline width/height during drag. Without this
					// the iframe stays at its old React-prop size until mouseup.
					innerEmbedContainerRef.current =
						container.querySelector<HTMLElement>('[data-testid="native-embed-extension"]') ?? null;

					// Sync resizeWidth to the actual DOM width so the first drag
					// starts from where the content visually is, not from the
					// potentially stale currentWidth (e.g. lineLength fallback).
					const rect = container.getBoundingClientRect();
					const newWidth = rect.width > 0 ? Math.round(rect.width) : null;

					setSelectedNativeEmbed(selected);
					setEmbedDomElement(container);
					if (newWidth !== null) {
						setResizeWidth(newWidth);
						const facade = getNativeEmbedFacade();
						const nodeUrl = getParameters(selected.node.attrs.parameters, 'url');
						let manifest = nodeUrl ? facade.getManifestByUrl(nodeUrl) : undefined;
						if (!manifest) {
							const extensionKey: string | undefined = selected.node.attrs.extensionKey;
							const experienceId = extensionKey?.split(':')[1];
							if (experienceId) {
								manifest = facade.getManifestById(experienceId);
							}
						}
					const shouldLockAspectRatio = manifest?.lockResizeAspectRatio ?? true;
					aspectRatioRef.current = shouldLockAspectRatio ? rect.height / rect.width : null;
					fixedHeightRef.current = shouldLockAspectRatio ? null : Math.round(rect.height);
				}
				} else {
					setSelectedNativeEmbed(selected);
				}
			} else if (selectedEmbedPos !== null) {
				setSelectedNativeEmbed(null);
			}
		});
	}, [selectedEmbedPos, view, view?.state]);

	// -----------------------------------------------------------------------
	// Derive current width from node attributes (layout-aware)
	// -----------------------------------------------------------------------

	const currentWidth = useMemo(() => {
		if (!selectedNativeEmbed || !view) {
			return akEditorDefaultLayoutWidth;
		}
		const node = selectedNativeEmbed.node;
		if (!node) {
			return akEditorDefaultLayoutWidth;
		}
		const storedWidth = getWidth(node);
		if (storedWidth) {
			return storedWidth;
		}

		// TODO: CNS-24595 - review this logic.
		const layout = node.attrs.layout || 'default';
		const paramWidth = node.attrs.parameters?.width;

		// Always use the persisted parameters.width when available — this is
		// the committed pixel width from the last resize operation.
		if (typeof paramWidth === 'number' && paramWidth > 0) {
			return paramWidth;
		}

		// Fallback when no parameters.width is set (first render / never resized)
		if (layout === 'wide') {
			return Math.ceil(lineLength * breakoutWideScaleRatio);
		}
		if (layout === 'full-width') {
			return akEditorFullWidthLayoutWidth;
		}
		return lineLength;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedNativeEmbed, lineLength]);

	// The max width of the parent node of the nested node,
	// OR undefined if the node is not nested
	const nestedMaxWidth = useMemo(() => {
		if (!selectedNativeEmbed || !view) {
			return undefined;
		}

		return getParentNodeWidth(selectedNativeEmbed.pos, view.state, {
			width: containerWidth,
			lineLength,
		});
	}, [selectedNativeEmbed, view, containerWidth, lineLength]);

	const isNested = nestedMaxWidth !== undefined;

	const maxWidth = useMemo(() => {
		return nestedMaxWidth ?? calcMaxWidth(containerWidth, appearance);
	}, [nestedMaxWidth, containerWidth, appearance]);

	// -----------------------------------------------------------------------
	// Resize state
	// -----------------------------------------------------------------------

	const [isResizing, setIsResizing] = useState(false);
	const [resizeWidth, setResizeWidth] = useState<number>(currentWidth);
	const [snaps, setSnaps] = useState<Snap>({});

	// Sync resizeWidth with currentWidth when not actively resizing.
	// Use a ref to track the last committed width so we don't snap back
	// to a stale currentWidth while the async extension API update propagates.
	const committedWidthRef = useRef<number | null>(null);
	const aspectRatioRef = useRef<number | null>(null);
	const fixedHeightRef = useRef<number | null>(null);
	const lastSnappedGuidelineKeysRef = useRef<string[]>([]);

	useEffect(() => {
		if (!isResizing) {
			// If we have a recently committed width, only sync once currentWidth
			// matches it (meaning the async update has propagated), or if the
			// selection changed entirely.
			if (committedWidthRef.current !== null) {
				if (currentWidth === committedWidthRef.current) {
					// Extension API update has propagated — clear the guard
					committedWidthRef.current = null;
				}
				// Don't override resizeWidth until propagation completes
				return;
			}
			// Clamp the display width to maxWidth so that a stored full-width value
			// (e.g. akEditorFullWidthLayoutWidth = 1800) doesn't overflow a narrow
			// viewport — the embed will expand to the full stored width when the
			// viewport is wider.
			setResizeWidth(Math.min(currentWidth, maxWidth));
		}
	}, [currentWidth, isResizing, maxWidth]);

	const [embedDomElement, setEmbedDomElement] = useState<HTMLElement | null>(null);
	const innerEmbedContainerRef = useRef<HTMLElement | null>(null);
	// For nested embeds, capture the actual pixel width of the parent container at
	// drag start. nestedMaxWidth is derived from ProseMirror schema attributes and may
	// not match the actual CSS-computed pixel width (e.g. layout columns with implicit
	// widths). This DOM measurement is the source of truth for clamping during drag.
	const nestedContainerWidthRef = useRef<number | null>(null);
	const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
	const [nodeRect, setNodeRect] = useState<NodeRect | null>(null);
	const [overlayHoverSurfaceElement, setOverlayHoverSurfaceElement] =
		useState<HTMLDivElement | null>(null);
	const [isHoveringResizeSurface, setIsHoveringResizeSurface] = useState(false);

	// Measure the embed DOM position for overlay positioning.
	// Only runs when selection changes or resize finishes — NOT during drag.
	// During drag, the overlay position is computed live in the render body.
	useLayoutEffect(() => {
		// Skip during active resize to avoid stale measurements from transform styles
		if (isResizing) {
			return;
		}

		if (!selectedNativeEmbed || !view) {
			setNodeRect(null);
			setPortalTarget(null);
			return;
		}

		try {
			const domNode = findDomRefAtPos(selectedNativeEmbed.pos, view.domAtPos.bind(view));
			const element =
				domNode instanceof HTMLElement ? domNode : (domNode.parentElement as HTMLElement | null);
			if (!element) {
				setNodeRect(null);
				setPortalTarget(null);
				return;
			}

			// Use .extension-container when present (matches floating toolbar anchor logic)
			const measureEl =
				(element.querySelector('.extension-container') as HTMLElement | null) ??
				(element.closest('.extension-container') as HTMLElement | null) ??
				element;

			// Use scroll container first, then content area
			const container =
				(view.dom.closest('.fabric-editor-popup-scroll-parent') as HTMLElement | null) ||
				(view.dom.closest('.ak-editor-content-area') as HTMLElement | null) ||
				view.dom.parentElement;
			if (!container) {
				setNodeRect(null);
				setPortalTarget(null);
				return;
			}

			setPortalTarget(container);

			const containerRect = container.getBoundingClientRect();
			const nodeRectRaw = measureEl.getBoundingClientRect();

			setNodeRect({
				top: nodeRectRaw.top - containerRect.top + container.scrollTop,
				left: nodeRectRaw.left - containerRect.left + container.scrollLeft,
				width: nodeRectRaw.width,
				height: nodeRectRaw.height,
			});
		} catch {
			setNodeRect(null);
			setPortalTarget(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedNativeEmbed, view, isResizing]);

	const isWithinResizeSurface = useCallback(
		(target: EventTarget | null) => {
			if (!(target instanceof Node)) {
				return false;
			}

			return !!embedDomElement?.contains(target) || !!overlayHoverSurfaceElement?.contains(target);
		},
		[embedDomElement, overlayHoverSurfaceElement],
	);

	useEffect(() => {
		if (!embedDomElement && !overlayHoverSurfaceElement) {
			setIsHoveringResizeSurface(false);
			return;
		}

		const handleMouseEnter = () => {
			setIsHoveringResizeSurface(true);
		};
		const handleMouseLeave = (event: MouseEvent) => {
			if (isWithinResizeSurface(event.relatedTarget)) {
				return;
			}

			setIsHoveringResizeSurface(false);
		};

		const unbindEmbedDomElement = embedDomElement
			? bindAll(embedDomElement, [
					{ type: 'mouseenter', listener: handleMouseEnter },
					{ type: 'mouseleave', listener: handleMouseLeave },
				])
			: null;
		const unbindOverlayHoverSurfaceElement = overlayHoverSurfaceElement
			? bindAll(overlayHoverSurfaceElement, [
					{ type: 'mouseenter', listener: handleMouseEnter },
					{ type: 'mouseleave', listener: handleMouseLeave },
				])
			: null;

		return () => {
			unbindEmbedDomElement?.();
			unbindOverlayHoverSurfaceElement?.();
		};
	}, [embedDomElement, overlayHoverSurfaceElement, isWithinResizeSurface]);

	// -----------------------------------------------------------------------
	// Guidelines
	// -----------------------------------------------------------------------

	const defaultGuidelines = useMemo(() => {
		return isNested ? [] : generateDefaultGuidelines(lineLength, containerWidth, false);
	}, [lineLength, containerWidth, isNested]);

	// Mutable ref so handleResize can read current guidelines without re-creating
	const guidelinesRef = useMemo(() => ({ current: defaultGuidelines }), [defaultGuidelines]);

	const isGuidelineEnabled = useMemo(() => {
		return !!pluginInjectionApi?.guideline;
	}, [pluginInjectionApi]);


	// -----------------------------------------------------------------------
	// Resize handlers (must be before early return — hooks must run unconditionally)
	// -----------------------------------------------------------------------

	// Prevent mousedown on resize handles from propagating to the editor,
	// which would deselect the native embed and unmount the overlay before
	// the drag can start.
	const handleWrapperMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		const target = e.target;
		if (!(target instanceof HTMLElement)) {
			return;
		}
		// Check if the click originated from within a resize handle element
		const isHandle =
			target.closest('.resizer-handle') ||
			target.closest('.resizer-handle-thumb') ||
			target.closest('.resizer-handle-track') ||
			target.closest('[class*="resizer-handle"]');
		if (isHandle) {
			// Set the guard early so shared state updates that arrive between
			// mousedown and onResizeStart don't null out selectedEmbedPos
			isResizingRef.current = true;
			e.stopPropagation();
		}
	}, []);

	const handleResizeStart = useCallback(() => {
		setIsResizing(true);
		isResizingRef.current = true;
		lastSnappedGuidelineKeysRef.current = [];

		// Grab a reference to the actual embed DOM element so we can set its
		// width directly during drag for smooth live preview.
		if (selectedNativeEmbed && view) {
			try {
				const domNode = findDomRefAtPos(selectedNativeEmbed.pos, view.domAtPos.bind(view));
				const element =
					domNode instanceof HTMLElement ? domNode : (domNode.parentElement as HTMLElement | null);
				if (element) {
					const container =
						(element.querySelector('.extension-container') as HTMLElement | null) ??
						(element.closest('.extension-container') as HTMLElement | null) ??
						element;
					setEmbedDomElement(container);

					// For nested embeds, capture the actual pixel width of the parent
					// container at drag start. nestedMaxWidth (from ProseMirror schema)
					// may not match the CSS-computed pixel width (e.g. layout columns
					// with implicit widths). We use offsetParent's clientWidth as the
					// true available width so that clamping during drag matches what
					// the browser actually renders.
					if (isNested) {
						const parentEl = container.offsetParent as HTMLElement | null;
						if (parentEl) {
							nestedContainerWidthRef.current = Math.round(parentEl.getBoundingClientRect().width);
						}
					} else {
						nestedContainerWidthRef.current = null;
					}

					// Also grab the inner EmbedFrameWrapper container so we can
					// update its inline width/height during drag. Without this
					// the iframe stays at its old React-prop size until mouseup.
					innerEmbedContainerRef.current =
						container.querySelector<HTMLElement>('[data-testid="native-embed-extension"]') ?? null;

					// Sync resizeWidth to the actual DOM width so the first drag
					// starts from where the content visually is, not from the
					// potentially stale currentWidth (e.g. lineLength fallback).
					const rect = container.getBoundingClientRect();
					if (rect.width > 0) {
						setResizeWidth(Math.round(rect.width));
						const facade = getNativeEmbedFacade();
						const nodeUrl = getParameters(selectedNativeEmbed.node.attrs.parameters, 'url');
						let manifest = nodeUrl ? facade.getManifestByUrl(nodeUrl) : undefined;
						if (!manifest) {
							const extensionKey: string | undefined = selectedNativeEmbed.node.attrs.extensionKey;
							const experienceId = extensionKey?.split(':')[1];
							if (experienceId) {
								manifest = facade.getManifestById(experienceId);
							}
						}
					const shouldLockAspectRatio = manifest?.lockResizeAspectRatio ?? true;
					if (shouldLockAspectRatio) {
						aspectRatioRef.current = rect.height / rect.width;
						fixedHeightRef.current = null;
						container.style.setProperty(
							'--native-embed-aspect-ratio',
							`${rect.width} / ${rect.height}`,
						);
					} else {
						aspectRatioRef.current = null;
						const currentHeight = Math.round(rect.height);
						fixedHeightRef.current = currentHeight;
						container.style.removeProperty('aspect-ratio');
						container.style.setProperty('height', `${currentHeight}px`);
						if (innerEmbedContainerRef.current) {
							innerEmbedContainerRef.current.style.removeProperty('aspect-ratio');
							innerEmbedContainerRef.current.style.setProperty('height', `${currentHeight}px`);
						}
					}
				}
			}
		} catch {
			// ignore – we'll just not do live width updates
		}

			const { state, dispatch } = view;
			const tr = state.tr;
			tr.setMeta(NATIVE_EMBED_PLUGIN_IS_RESIZING_KEY, true);
			tr.setMeta('is-resizer-resizing', true);
			tr.setMeta('scrollIntoView', false);
			tr.setSelection(NodeSelection.create(state.doc, selectedNativeEmbed.pos));
			dispatch(tr);
		}
	}, [view, selectedNativeEmbed, isNested]);

	const handleResize: HandleResize = useCallback(
		(size: Position & Dimensions, delta: Dimensions) => {
			const newWidth = Math.round(size.width + delta.width);
			// Clamp width to maxWidth so the embed never extends past the
			// container boundary — matching media single behaviour.
			// For nested embeds, also clamp against the actual DOM pixel width
			// of the parent container measured at drag start, which is the
			// ground truth (nestedMaxWidth from ProseMirror schema may differ).
			const effectiveMax =
				isNested && nestedContainerWidthRef.current !== null
					? Math.min(maxWidth, nestedContainerWidthRef.current)
					: maxWidth;
			const clampedWidth = Math.max(MIN_WIDTH, Math.min(newWidth, effectiveMax));

			setResizeWidth(clampedWidth);

			// Auto-switch to center alignment when resizing past the threshold
			// while left or right aligned. This ensures the toolbar updates
			// immediately and the resize switches to both-sided behavior.
			const isSideAligned =
				currentAlignment === 'left' ||
				currentAlignment === 'right' ||
				currentAlignment === 'wrap-left' ||
				currentAlignment === 'wrap-right';
			if (!isNested && isSideAligned && clampedWidth > lineLength) {
				setCurrentAlignment('center');
				if (selectedNativeEmbed?.node && view?.state && view?.dispatch) {
					createUpdateAlignmentCommand(
						pluginInjectionApi,
						'center',
						selectedNativeEmbed.node,
					)(view?.state, view?.dispatch);
				}
			}

			// Update active guidelines and track which guidelines are snapped
			// so handleResizeStop can detect full-width snaps.
			if (isGuidelineEnabled) {
				const layoutForGuidelines = mapAlignmentToLayout(currentAlignment);
				const guidelineSnaps = getGuidelineSnaps(
					guidelinesRef.current,
					lineLength,
					layoutForGuidelines,
				);

				if (guidelineSnaps.snaps.x) {
					const { gap, keys: activeGuidelineKeys } = findClosestSnap(
						clampedWidth,
						guidelineSnaps.snaps.x,
						guidelineSnaps.guidelineReference,
						SNAP_GAP,
					);

					lastSnappedGuidelineKeysRef.current = activeGuidelineKeys;

					const nextGuideLines = getGuidelinesWithHighlights(
						gap,
						SNAP_GAP,
						activeGuidelineKeys,
						guidelinesRef.current,
					);
					view &&
						pluginInjectionApi?.guideline?.actions?.displayGuideline(view)({
							guidelines: nextGuideLines,
						});
				}

				setSnaps({
					x: guidelineSnaps.snaps.x || [],
				});
			}

			// Directly set the embed DOM element's inline styles for smooth live preview.
			// For nested embeds, avoid setting width/minWidth on the outer container because
			// that can force it to overflow its parent while dragging. Instead, drive sizing
			// via the CSS variable + maxWidth, which matches the committed nested behaviour.
			// For top-level embeds, continue using explicit width/minWidth/maxWidth so the
			// live preview tracks the drag exactly.
			if (embedDomElement) {
				embedDomElement.style.transition = 'none';
				if (isNested) {
					// For nested embeds, do NOT set an explicit width — doing so would
					// override the parent cell's layout constraints and let the embed
					// overflow. Instead, drive sizing only through maxWidth so CSS
					// and the parent container naturally clamp the visual size.
					embedDomElement.style.width = '';
					embedDomElement.style.minWidth = '';
					embedDomElement.style.maxWidth = `${clampedWidth}px`;
					embedDomElement.style.setProperty(CSS_VAR_NATIVE_EMBED_WIDTH, `${clampedWidth}px`);
				} else {
					embedDomElement.style.width = `${clampedWidth}px`;
					embedDomElement.style.minWidth = `${clampedWidth}px`;
					embedDomElement.style.maxWidth = `${clampedWidth}px`;
					embedDomElement.style.removeProperty(CSS_VAR_NATIVE_EMBED_WIDTH);
				}
			}

			// Also update the inner EmbedFrameWrapper container so the iframe
			// (which uses width/height: 100%) fills the new dimensions during drag.
			const innerContainer = innerEmbedContainerRef.current;
			if (innerContainer) {
				innerContainer.style.width = `${clampedWidth}px`;
			}
		},
		[
			maxWidth,
			isNested,
			lineLength,
			guidelinesRef,
			isGuidelineEnabled,
			embedDomElement,
			currentAlignment,
			selectedNativeEmbed,
			pluginInjectionApi,
			view,
		],
	);

	const handleResizeStop: HandleResize = useCallback(
		(_size: Position & Dimensions, _delta: Dimensions) => {
			// Determine whether the user snapped to the full-width guideline.
			// When they did, store akEditorFullWidthLayoutWidth (1800) rather
			// than the actual viewport pixel width — this mirrors the media
			// single behaviour so that the embed expands correctly when the
			// viewport grows later.
			const isFullWidthSnap = lastSnappedGuidelineKeysRef.current.some((key) =>
				key.startsWith('full_width'),
			);
			const committedWidth =
				!isNested && isFullWidthSnap ? akEditorFullWidthLayoutWidth : resizeWidth;

			setIsResizing(false);
			committedWidthRef.current = committedWidth;

			// Clear the inline width/minWidth styles set during drag so that
			// the CSS variable (--native-embed-width) and stylesheet rules take over.
			// This allows the embed to respond correctly to future viewport changes.
			// Restore height: auto and max-width to the values that index.tsx sets on
			// the extension-container — clearing them entirely would remove properties
			// that index.tsx expects to persist, causing layout issues after resize.
			// For unlocked-aspect-ratio embeds (e.g. databases), restore the fixed
			// pixel height instead of auto so the height doesn't collapse between
			// handleResizeStop and the React re-render.
			if (embedDomElement) {
				embedDomElement.style.transition = '';
				embedDomElement.style.width = '';
				embedDomElement.style.minWidth = '';
				embedDomElement.style.setProperty('max-width', 'var(--ak-editor-max-container-width)');
				if (fixedHeightRef.current !== null) {
					embedDomElement.style.setProperty('height', `${fixedHeightRef.current}px`);
					embedDomElement.style.removeProperty('aspect-ratio');
				} else {
					embedDomElement.style.setProperty('height', 'auto');
				}
				embedDomElement.style.setProperty(CSS_VAR_NATIVE_EMBED_WIDTH, `${committedWidth}px`);
			}

			// Clear inline styles on the inner container too so the iframe
			// fills whatever the CSS-driven container width ends up being.
			// For unlocked-aspect-ratio embeds, preserve the fixed pixel height
			// so the iframe doesn't collapse to 0 before the React re-render.
			const innerContainer = innerEmbedContainerRef.current;
			if (innerContainer) {
				innerContainer.style.width = '';
				if (fixedHeightRef.current !== null) {
					innerContainer.style.height = `${fixedHeightRef.current}px`;
					innerContainer.style.removeProperty('aspect-ratio');
				} else {
					innerContainer.style.height = '';
				}
			}
			innerEmbedContainerRef.current = null;

			// Clear guidelines and tracked snap keys
			view && pluginInjectionApi?.guideline?.actions?.displayGuideline(view)({ guidelines: [] });
			lastSnappedGuidelineKeysRef.current = [];

			if (!selectedNativeEmbed || !view) {
				isResizingRef.current = false;
				return;
			}

			// Read the node fresh from the current state (not the stale closure)
			const currentNode = selectedNativeEmbed.node;
			if (!currentNode) {
				isResizingRef.current = false;
				return;
			}

			// Commit width and layout to the document.
			// For locked-aspect-ratio embeds, derive the height from the ratio.
			// For unlocked embeds, commit the fixed height captured at resize start
			// so the ADF node retains the correct height parameter.
			const committedHeight =
				aspectRatioRef.current !== null
					? Math.round(committedWidth * aspectRatioRef.current)
					: fixedHeightRef.current ?? undefined;
			createUpdateWidthCommand(
				pluginInjectionApi,
				committedWidth,
				selectedNativeEmbed.node,
				committedHeight,
			)(view.state, view.dispatch);

			// Clear resizing plugin state
			const { state, dispatch } = view;
			const tr = state.tr;
			tr.setMeta(NATIVE_EMBED_PLUGIN_IS_RESIZING_KEY, false);
			tr.setMeta('is-resizer-resizing', false);
			tr.setMeta('scrollIntoView', false);
			dispatch(tr);

			// Release the resize guard after a frame so that the shared state
			// subscription ignores the transaction-triggered updates above.
			// Without this, the subscription would fire immediately with stale
			// DOM measurements, causing the handles to jump back.
			requestAnimationFrame(() => {
				isResizingRef.current = false;
			});
			aspectRatioRef.current = null;
			fixedHeightRef.current = null;
		},
		[selectedNativeEmbed, pluginInjectionApi, view, embedDomElement, resizeWidth, isNested],
	);

	// -----------------------------------------------------------------------
	// Early return — nothing to render (after all hooks)
	// -----------------------------------------------------------------------

	if (
		(selectedEmbedPos === null && !isHoveringResizeSurface && !isResizing) ||
		!view ||
		!nodeRect ||
		!portalTarget ||
		!selectedNativeEmbed
	) {
		return null;
	}

	const isRightTypeAligned = currentAlignment === 'right' || currentAlignment === 'wrap-right';
	const isLeftTypeAligned = currentAlignment === 'left' || currentAlignment === 'wrap-left';

	const enableRightHandle =
		!disableHandles && currentAlignment !== 'right' && currentAlignment !== 'wrap-right';
	const enableLeftHandle =
		!disableHandles && currentAlignment !== 'left' && currentAlignment !== 'wrap-left';

	// -----------------------------------------------------------------------
	// Overlay positioning — center over the embed
	// -----------------------------------------------------------------------

	let wrapperLeft: number;
	// overlayWidth drives both the outer wrapper div and the ResizerNext width prop.
	// resizeWidth is already clamped to maxWidth (from ProseMirror schema) in handleResize.
	// For nested embeds we also cap against the actual DOM pixel width of the parent
	// container captured at drag start — this is the ground truth of available width,
	// since nestedMaxWidth (from ProseMirror attrs) may not match the CSS layout width.
	const actualNestedMax = nestedContainerWidthRef.current;
	const overlayWidth = isResizing
		? isNested && actualNestedMax !== null
			? Math.min(resizeWidth, actualNestedMax)
			: resizeWidth
		: Math.round(nodeRect.width);

	if (isResizing && embedDomElement && (isLeftTypeAligned || isRightTypeAligned)) {
		// For left/right aligned embeds during resize, read the actual DOM position
		// to keep handles attached to the iframe edges as width changes
		const portalRect = portalTarget.getBoundingClientRect();
		const embedRect = embedDomElement.getBoundingClientRect();
		wrapperLeft = embedRect.left - portalRect.left + portalTarget.scrollLeft;
	} else if (isResizing && embedDomElement) {
		// For center alignment during resize, read the left edge of the embed from DOM.
		// For non-nested embeds, centerX is derived from the embed's actual DOM center
		// (which tracks the drag symmetrically). For nested embeds, the embed container
		// takes full cell width (its inline width is cleared, only maxWidth is set), so
		// we use the embed's left edge and the resizeWidth to position the overlay so
		// handles track the visible content edges rather than the full cell width.
		const portalRect = portalTarget.getBoundingClientRect();
		const embedRect = embedDomElement.getBoundingClientRect();
		if (isNested) {
			// For nested embeds, the embed container has width:'' + maxWidth:clampedWidth,
			// so embedRect.width = min(cellWidth, clampedWidth). overlayWidth is also
			// derived from embedRect.width (above), so the overlay exactly matches the
			// visible content. Left-align the overlay with the embed's left edge.
			wrapperLeft = embedRect.left - portalRect.left + portalTarget.scrollLeft;
		} else {
			const centerX =
				embedRect.left - portalRect.left + portalTarget.scrollLeft + embedRect.width / 2;
			wrapperLeft = centerX - overlayWidth / 2;
		}
	} else if (isRightTypeAligned || isLeftTypeAligned) {
		// When not resizing, use nodeRect position directly for aligned embeds
		wrapperLeft = nodeRect.left;
	} else {
		// Center alignment when not resizing - calculate from nodeRect center
		const centerX = nodeRect.left + nodeRect.width / 2;
		wrapperLeft = centerX - overlayWidth / 2;
	}

	const resizerWrapperDynamicStyles = {
		top: nodeRect.top,
		left: wrapperLeft,
		width: overlayWidth,
	};

	const isHandleVisible =
		!disableHandles && nodeRect.height && (isResizing || isHoveringResizeSurface);

	/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic overlay positioning from DOM measurements */
	const overlay = (
		<div
			data-testid="native-embed-resizer"
			role="presentation"
			ref={setOverlayHoverSurfaceElement}
			onMouseDown={handleWrapperMouseDown}
		>
			<div
				css={[resizerWrapperStyles]}
				style={resizerWrapperDynamicStyles}
				data-testid="native-embed-resizer-wrapper"
			>
				<ResizerNext
					/* eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) */
					enable={{
						left: enableLeftHandle,
						right: enableRightHandle,
					}}
					width={overlayWidth}
					minWidth={MIN_WIDTH}
					maxWidth={isNested && actualNestedMax !== null ? actualNestedMax : maxWidth}
					handleResizeStart={handleResizeStart}
					handleResize={handleResize}
					handleResizeStop={handleResizeStop}
					snapGap={SNAP_GAP}
					snap={snaps}
					resizeRatio={currentAlignment === 'center' ? 2 : 1}
					isHandleVisible={!!isHandleVisible}
					handleHighlight="full-height"
					data-testid={resizerNextTestId}
				>
					<div
						css={resizerPlaceholderStyles}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic height from DOM
						style={{
							height:
								isResizing && aspectRatioRef.current !== null
									? Math.round(resizeWidth * aspectRatioRef.current)
									: nodeRect.height || 600,
						}} // TODO: CNS-24595 - 600 is a hack, it can lose the select in cases and just falls back to this. We can do better
					/>
				</ResizerNext>
			</div>
		</div>
	);

	/* eslint-enable @atlaskit/ui-styling-standard/enforce-style-prop */
	return ReactDOM.createPortal(overlay, portalTarget);
};
